import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import axios from 'axios';
import NarrativeEditor from '../components/wizard/NarrativeEditor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Narrator Page - Standalone AI Report Narrator
 * Accessible from report detail page (Clerk auth) or accountant portal (accountant auth)
 * Loads existing narrative if available
 */
export default function NarratorPage() {
  const { id, reportId } = useParams();
  const location = useLocation();
  const { getToken } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Check if this is accountant route
  const isAccountantRoute = location.pathname.startsWith('/accountant/');
  const actualReportId = reportId || id;

  useEffect(() => {
    const fetchReport = async () => {
      try {
        let headers = {};
        
        if (isAccountantRoute) {
          // Use accountant token from localStorage
          const accountantToken = localStorage.getItem('accountant_token');
          if (accountantToken) {
            headers = { Authorization: `Bearer ${accountantToken}` };
          }
          
          // Fetch from collaboration endpoint
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/collaboration/reports/${actualReportId}/view`,
            { headers }
          );
          setReport(response.data);
        } else {
          // Use Clerk token
          const token = await getToken();
          headers = { Authorization: `Bearer ${token}` };
          
          // Fetch from regular endpoint
          const response = await axios.get(
            `${BACKEND_URL}/api/v1/reports/${actualReportId}`,
            { headers }
          );
          setReport(response.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch report:', err);
        setLoading(false);
      }
    };

    if (actualReportId) {
      fetchReport();
    }
  }, [actualReportId, getToken, isAccountantRoute]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800">Report not found</h2>
          <p className="text-gray-600 mt-2">Unable to load claim data</p>
        </div>
      </div>
    );
  }

  return (
    <NarrativeEditor
      claimId={actualReportId || ''}
      claimData={{
        client_name: report.client_name,
        claimant_name: report.claimant_name,
        business_type: report.business_type || 'Unknown',
        zone: report.zone || 'Unknown'
      }}
      calculationResults={{
        ...(report.result || {}),
        sensitivity_analysis: report.sensitivity_analysis,
        monte_carlo_analysis: report.monte_carlo_analysis
      }}
      eventType={report.event_type || 'Hurricane'}
      existingNarrative={report.narrative || null}
      isAccountantView={isAccountantRoute}
    />
  );
}
